<template>
  <AccountConfigurationQuestionare
    ref="form"
    nextButtonLabel="common.button.submit"
    :nextButtonOnClick="nextButtonOnClick"
    :backButtonOnClick="backButtonOnClick"
    :form="form"
    :rules="rules"
    :tradingPlatformOptions="tradingPlatformOptions"
    :accountTypeOptions="accountTypeOptions"
    :currencyOptions="currencyOptions"
    :tradingPlatformOnChange="tradingPlatformOnChange"
    :accountTypeOnChange="accountTypeOnChange"
    :abTestingDisplayPage="abTestingDisplayPage"
    :showSetPassword="true"
    verticalPadding="py-0"
  />
</template>

<script>
import registerStep, { getUserStep } from '@/constants/register/registerStep';
import registerRepository from '@/repositories/register/registerRepository';
import AccountConfigurationQuestionare from '@/components/register/openAccount/accountConfiguration/AccountConfigurationQuestionare.vue';
import { checkAgreedTNC } from '@/util/validation';
import auditStatus from '@/constants/auditStatus';
import { endAjaxLoading, startLoading } from '@/util/loading';
import { sensorsTrack } from '@/util/sensorsdata';
import { SENSORS_TRACK } from '@/util/sensorsdata/constant';
import { AbTemplateDisplayPage } from '@/constants/abTesting';
import { encryptRSA } from '@/util/encrypt';

export default {
  name: 'AccountConfiguration',
  components: {
    AccountConfigurationQuestionare
  },
  data() {
    return {
      form: {
        tradingPlatform: null,
        accountType: null,
        currency: null,
        checkStatus: false,
        password: null,
      },
      eligibleCurrency: 'All',
      showUsc: false,
      rules: {
        tradingPlatform: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('openAdditAcc.platErr')
          }
        ],
        accountType: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('openAdditAcc.accTypeErr')
          }
        ],
        currency: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('openAdditAcc.currError')
          }
        ],
        checkStatus: [
          {
            validator: checkAgreedTNC,
            trigger: 'change'
          }
        ]
      },
      accountTypeList: {
        mt4: [],
        mt5: []
      }
    };
  },
  computed: {
    tradingPlatformOptions() {
      // temp (from regulator)
      return this.$config.openLiveAccount
        .choosePlatform(this.regulator)
        .filter(platform => this.accountTypeList[platform.value].length > 0);
    },
    accountTypeOptions() {
      return this.accountTypeList[this.form.tradingPlatform] ?? [];
    },
    currencyOptions() {
      return this.$config.openLiveAccount.chooseCurrency(
        this.regulator,
        this.form.tradingPlatform,
        this.form.accountType,
        this.$store.state.common.countryCode,
        this.eligibleCurrency,
        this.showUsc
      );
    }
  },
  created() {
    this.abTestingDisplayPage = AbTemplateDisplayPage.OPEN_LIVE_ACCOUNT_AA;
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      const responses = await Promise.all([registerRepository.getData(4), this.getAccountTypes()]);

      const userData = responses[0];

      if (userData instanceof Error) {
        this.$message({
          message: userData.message,
          type: 'error'
        });
      } else {
        const { tradingPlatform, accountType, currency } = userData;
        this.form = {
          tradingPlatform: tradingPlatform ?? null,
          accountType: accountType ?? null,
          currency: currency ?? null,
          checkStatus: tradingPlatform !== null && accountType !== null && currency !== null
        };
      }

      // Default accountType
      if (!this.form.tradingPlatform && this.tradingPlatformOptions?.length > 0) {
        this.tradingPlatformOnChange(this.tradingPlatformOptions[0].value);
      }
    },
    async getAccountTypes() {
      const accountTypes = await registerRepository.getAccountType();

      if (accountTypes instanceof Error) {
        this.$message({
          message: accountTypes.message,
          type: 'error'
        });
      } else {
        // special handling for showing usc
        this.showUsc = accountTypes.showUSC;

        // temp (from regulator)
        const registerAccountTypes = this.$config.openLiveAccount.chooseRegisterType(
          this.regulator,
          this.$store.state.common.countryCode,
          this.showUsc
        );

        this.accountTypeList = {
          mt4: registerAccountTypes.filter(type => accountTypes.mt4.includes(type.value)),
          mt5: registerAccountTypes.filter(type => accountTypes.mt5.includes(type.value))
        };
      }
    },
    tradingPlatformOnChange(value) {
      this.form.tradingPlatform = value;
      this.form.accountType = null;
      this.form.currency = null;
    },
    accountTypeOnChange(value) {
      this.form.accountType = value;
      this.form.currency = null;
    },
    async nextButtonOnClick() {
      sensorsTrack(SENSORS_TRACK.EVENT_NAME.OPEN_IDENTITY_PAGE_CLICK, {
        openidentity_level: SENSORS_TRACK.IDENTITY_LEVEL.LV1,
        openidentity_step: SENSORS_TRACK.IDENTITY_STEP.LV1_ACCOUNT_CONFIGURATION,
        button_name: SENSORS_TRACK.BUTTON_NAME.LV1_NEXT
      })
      this.$refs.form?.validate(async valid => {
        if (valid) {
          const { tradingPlatform, accountType, currency, password } = this.form;

          startLoading('ajax');
          const response = await registerRepository.process({
            step: getUserStep(registerStep.accountConfiguration),
            form: { tradingPlatform, accountType, currency, password: encryptRSA(password) }
          });

          if (response instanceof Error) {
            this.$message({
              message: response.message,
              type: 'error'
            });
          } else {
            this.$refs.form?.submitAbTesting?.();
            const currAccountAuditStatus = await this.$store.dispatch('common/actionAccountAuditStatus');

            if (currAccountAuditStatus !== auditStatus.completed) {
              // this.$router.push(RegisterRoutePath.identityVerification);
              // ACM-32640需求中需加上此逻辑 不再直接跳转到identityVerification
              this.$store.dispatch('register/setActiveTab', registerStep.openAccountStatus);
            }
            this.$store.dispatch('register/setStep', registerStep.openAccountStatus);
          }
          endAjaxLoading();
        } else {
          console.log('error submit!!');
        }
      });
    },
    backButtonOnClick() {
      sensorsTrack(SENSORS_TRACK.EVENT_NAME.OPEN_IDENTITY_PAGE_CLICK, {
        openidentity_level: SENSORS_TRACK.IDENTITY_LEVEL.LV1,
        openidentity_step: SENSORS_TRACK.IDENTITY_STEP.LV1_ACCOUNT_CONFIGURATION,
        button_name: SENSORS_TRACK.BUTTON_NAME.LV1_BACK
      })
      this.$store.dispatch('register/setActiveTab', registerStep.personalInformation);
    }
  }
};
</script>
